import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useUpdateChannel } from "@/api/mutations/channels";
import {
  validateUpdateChannel,
  parseConfig,
  type ValidationError,
} from "@/api/schemas/channel";
import type { Channel } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: Channel;
}

export function UpdateChannelDialog({ open, onOpenChange, channel }: Props) {
  const [name, setName] = useState(channel.name);
  const [configJson, setConfigJson] = useState(JSON.stringify(channel.config ?? {}, null, 2));
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useUpdateChannel();

  useEffect(() => {
    if (open) {
      setName(channel.name);
      setConfigJson(JSON.stringify(channel.config ?? {}, null, 2));
      setErrors([]);
      setServerError(null);
    }
  }, [open, channel.name, channel.config]);

  function fieldError(f: ValidationError["field"]) {
    return errors.find((e) => e.field === f)?.message;
  }

  function handleSubmit() {
    const v = validateUpdateChannel({ name, configJson });
    setErrors(v);
    setServerError(null);
    if (v.length > 0) return;

    const parsed = parseConfig(configJson);
    if (!parsed.ok) return;

    mutation.mutate(
      { id: channel.id, name, config: parsed.value },
      {
        onSuccess: () => {
          toast.success("Channel aktualisiert");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Update fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="update-channel-dialog">
        <DialogHeader>
          <DialogTitle>Channel bearbeiten</DialogTitle>
          <DialogDescription>
            Type ist immutable. Name + Config sind editierbar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="update-name">Name</Label>
            <Input
              id="update-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="update-field-name"
            />
            {fieldError("name") && (
              <div className="text-xs text-red">{fieldError("name")}</div>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="update-config">Config (JSON)</Label>
            {(channel.secretConfigKeys?.length ?? 0) > 0 && (
              <p className="text-[11.5px] text-text3" data-testid="update-config-secret-hint">
                {channel.secretConfigKeys?.join(", ")} {(channel.secretConfigKeys?.length ?? 0) === 1 ? "ist" : "sind"} als geheim hinterlegt und deshalb hier nicht sichtbar — beim Speichern bleibt der Wert erhalten.
              </p>
            )}
            <Textarea
              id="update-config"
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              rows={8}
              className="font-mono text-xs"
              data-testid="update-field-config"
            />
            {fieldError("configJson") && (
              <div className="text-xs text-red" data-testid="update-error-config">
                {fieldError("configJson")}
              </div>
            )}
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="update-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="update-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            data-testid="update-submit"
          >
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
